import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useGetPriceAnalysisMutation } from 'services/api/cars.api';
import numberFormat from 'services/format/numberFormat';
import priceFormat from 'services/format/priceFormat';
import type { CarComparisonCar } from 'services/types/car';
import { SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import capitalize from 'utils/capitalize';
import { normalCDF } from 'utils/normalCDF';

// Design system chart colors
const CHART_COLOR_MANUAL = '#2C5889'; // Blue
const CHART_COLOR_AUTOMATIC = '#F9D00A'; // Yellow
const CHART_COLOR_CURRENT = '#E7233B'; // Error red
const CHART_COLOR_REGRESSION = '#25616F'; // Teal

interface PriceAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  carId: number;
}

// Custom tooltip content component
const ChartTooltipContent: React.FC = () => {
  const tooltipData = useItemTooltip<'scatter'>();

  if (!tooltipData) return null;

  const { value, identifier, label } = tooltipData;
  const x = value?.x ?? 0;
  const y = value?.y ?? 0;
  const xDisplay = x < 0 ? `${numberFormat(-10 * x)} (Valor combinado entre km y años de uso)` : numberFormat(x);

  if (identifier.seriesId === 'regression') {
    return (
      <Box sx={{ p: 1.5, backgroundColor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        <Typography variant="body2">Línea de regresión</Typography>
        <Typography variant="body2">Valor: {xDisplay}</Typography>
        <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      </Box>
    );
  }

  if (identifier.seriesId === 'current') {
    return (
      <Box sx={{ p: 1.5, backgroundColor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        <Typography variant="body2">Auto actual</Typography>
        <Typography variant="body2">Valor: {xDisplay}</Typography>
        <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      </Box>
    );
  }

  const transmissionText = identifier.seriesId === 'cars-automatic' ? 'Automático' : 'Manual';

  return (
    <Box sx={{ p: 1.5, backgroundColor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2">Valor: {xDisplay}</Typography>
      <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      <Typography variant="body2">Transmisión: {transmissionText}</Typography>
    </Box>
  );
};

const CustomTooltip: React.FC = (props) => {
  return (
    <ChartsTooltipContainer {...props} trigger="item">
      <ChartTooltipContent />
    </ChartsTooltipContainer>
  );
};

type ComparisonType = 'general' | 'same-transmission' | 'most-similar' | 'db' | 'chileautos' | 'mercadolibre';

type ComparisonCarWithXValue = CarComparisonCar & { x: number };
type Attribute = 'km' | 'usageYears' | 'combined';
type SimplifiedCar = { km?: number | null; usageYears?: number | null };

const getXAxisLabel = (attr: Attribute): string => {
  switch (attr) {
    case 'km':
      return 'Kilometraje';
    case 'usageYears':
      return 'Años de uso';
    case 'combined':
      return 'Combinación (Km y Años)';
    default:
      return 'Kilometraje';
  }
};

const getConfidenceColor = (rSquared: number, carCount: number): string => {
  if (rSquared > 0.5 && carCount > 6) return '#25616F'; // Teal — high
  if (rSquared > 0.2 && carCount > 3) return '#CF5C09'; // Warning — medium
  return '#E7233B'; // Error — low
};

const getConfidenceLabel = (rSquared: number, carCount: number): string => {
  if (rSquared > 0.5 && carCount > 6) return 'Alta confiabilidad';
  if (rSquared > 0.2 && carCount > 3) return 'Confiabilidad media';
  return 'Baja confiabilidad';
};

const PriceAnalysisModal: React.FC<PriceAnalysisModalProps> = ({ open, onClose, carId }) => {
  const [comparison, setComparison] = useState<ComparisonType>('general');
  const [getPriceAnalysis, { data, isLoading }] = useGetPriceAnalysisMutation();
  const [attribute, setAttribute] = useState<Attribute>('combined');
  const [carPrice, setCarPrice] = useState<number>(0);
  const [carKm, setCarKm] = useState<number>(0);
  const [carUsageYears, setCarUsageYears] = useState<number>(0);
  const [customAnalysis, setCustomAnalysis] = useState<boolean>(false);
  const [showRegressions, setShowRegressions] = useState<boolean>(false);

  useEffect(() => {
    getPriceAnalysis({ carId }).catch((error) => {
      console.error('Error fetching price analysis', error);
    });
  }, [carId]);

  useEffect(() => {
    if (data?.car) {
      setCarPrice(data.car.price);
      setCarKm(data.car.km);
      setCarUsageYears(data.car.usageYears || 0);
    }
  }, [data?.car]);

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Análisis de Precio</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const results = data?.comparison.find((c) => c.source === comparison);
  if (!data || !results) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Análisis de Precio</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6,
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            No se pudo obtener el análisis de precio
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const calculateXValue = (car: SimplifiedCar): number => {
    switch (attribute) {
      case 'usageYears':
        return car.usageYears || 0;
      case 'combined':
        return (
          results.multiExponentialRegression.bKm * (car.km || 0) +
          results.multiExponentialRegression.bUsageYears * (car.usageYears || 0)
        );
      default:
        return car.km || 0;
    }
  };

  const calculateRegressionY = (xValue: number): number => {
    if (attribute === 'km') {
      return results.exponentialRegressionKm.a * Math.exp(results.exponentialRegressionKm.b * xValue);
    }
    if (attribute === 'usageYears') {
      return results.exponentialRegressionUsageYears.a * Math.exp(results.exponentialRegressionUsageYears.b * xValue);
    }
    return results.multiExponentialRegression.a * Math.exp(xValue);
  };

  // Extract automatic and manual cars
  const manualCars: ComparisonCarWithXValue[] =
    results.cars
      .filter((c) => c.transmission !== 'AUTOMATICA')
      .map((c) => ({
        ...c,
        x: calculateXValue(c),
      })) || [];
  const automaticCars: ComparisonCarWithXValue[] =
    results.cars
      .filter((c) => c.transmission === 'AUTOMATICA')
      .map((c) => ({
        ...c,
        x: calculateXValue(c),
      })) || [];

  // Handle item click to open the link
  const handleItemClick = (_: unknown, item: unknown) => {
    if (!item || !results) return;

    const { seriesId, dataIndex } = item as { seriesId: string; dataIndex: number };
    let link;
    let id;

    if (seriesId === 'cars-manual' && manualCars[dataIndex]) {
      link = manualCars[dataIndex].link;
      id = manualCars[dataIndex].id;
    } else if (seriesId === 'cars-automatic' && automaticCars[dataIndex]) {
      link = automaticCars[dataIndex].link;
      id = automaticCars[dataIndex].id;
    }

    if (id) {
      window.open(`/cars?id=${id}&analyzePrice=true`, '_blank');
    } else if (link) {
      window.open(link, '_blank');
    }
  };

  const generalCarsLength = data.comparison.find((comp) => comp.source === 'general')?.cars.length || 0;
  const sameTransmissionCarsLength =
    data.comparison.find((comp) => comp.source === 'same-transmission')?.cars.length || 0;
  const mostSimilarCarsLength = data.comparison.find((comp) => comp.source === 'most-similar')?.cars.length || 0;
  const dbCarsLength = data.comparison.find((comp) => comp.source === 'db')?.cars.length || 0;
  const mercadolibreCarsLength = data.comparison.find((comp) => comp.source === 'mercadolibre')?.cars.length || 0;
  const chileautosCarsLength = data.comparison.find((comp) => comp.source === 'chileautos')?.cars.length || 0;

  const rSquared = results.multiExponentialRegression.rSquared;
  const confidenceColor = getConfidenceColor(rSquared, results.cars.length);
  const confidenceLabel = getConfidenceLabel(rSquared, results.cars.length);

  // Price deviation
  const hasPrediction = !!results.predictedPrice;
  const percentile = hasPrediction ? normalCDF(results.priceDeviation / results.standardDeviationOfResiduals) * 100 : 0;
  const deviationPercent = hasPrediction ? Math.round(results.priceDeviation * 100) : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          Análisis de Precio
          <Chip
            label={`${data.car.brand} ${data.car.model} ${data.car.version || ''}`.trim()}
            size="small"
            sx={{ fontWeight: 500 }}
          />
          {data.car.link && (
            <Link
              href={data.car.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}
            >
              <OpenInNewIcon sx={{ fontSize: '0.9rem', mr: 0.3 }} />
              Ver publicación
            </Link>
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pb: 3,
          backgroundColor: SURFACE_CONTAINER_LOW,
          overflowX: 'auto',
          '& > *': {
            minWidth: { xs: '800px', sm: 'auto' },
          },
        }}
      >
        {/* Car Details + Key Metrics Row */}
        <Box sx={{ display: 'flex', gap: 2.5, mb: 3.5, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Car Info */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              Vehículo
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
              <Chip
                label={priceFormat.format(data.car.price || 0)}
                color="secondary"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              {data.car.businessUnit?.name && (
                <Chip label={data.car.businessUnit.name} variant="outlined" size="small" />
              )}
              <Chip label={`${numberFormat(data.car.km || 0)} km`} variant="outlined" size="small" />
              <Chip label={data.car.transmission} variant="outlined" size="small" />
              <Chip label={data.car.fuelType} variant="outlined" size="small" />
              <Chip
                label={`${data.car.year} (${data.car.usageYears?.toFixed(1)} años)`}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          {/* Prediction Result — the key insight */}
          {hasPrediction && !customAnalysis && (
            <Box
              sx={{
                minWidth: 280,
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                borderRadius: 3,
                p: 3,
                boxShadow: '0px 16px 40px rgba(21, 24, 58, 0.10)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Precio Predicho
              </Typography>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1 }}>
                {priceFormat.format(results.predictedPrice)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${deviationPercent > 0 ? '+' : ''}${numberFormat(deviationPercent)}% vs predicho`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: deviationPercent > 0 ? 'error.light' : 'success.light',
                    color: deviationPercent > 0 ? 'error.main' : 'success.main',
                  }}
                />
                <Chip
                  label={confidenceLabel}
                  size="small"
                  sx={{ fontWeight: 500, backgroundColor: `${confidenceColor}18`, color: confidenceColor }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {percentile < 50 ? 'Más caro' : 'Más barato'} que el{' '}
                {(percentile < 50 ? 100 - percentile : percentile).toFixed(0)}% de autos similares
              </Typography>
            </Box>
          )}
        </Box>

        {/* Controls Card */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3.5,
            boxShadow: SHADOW,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {/* Data Source */}
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
              Fuente de datos
            </Typography>
            <ToggleButtonGroup
              value={comparison}
              exclusive
              onChange={(_, newValue) => {
                if (!newValue) return;
                setComparison(newValue as ComparisonType);
              }}
              size="small"
            >
              <ToggleButton value="general" disabled={generalCarsLength === 0}>
                Todos ({generalCarsLength})
              </ToggleButton>
              <ToggleButton value="same-transmission" disabled={sameTransmissionCarsLength === 0}>
                {data.car.transmission} ({sameTransmissionCarsLength})
              </ToggleButton>
              <ToggleButton value="most-similar" disabled={mostSimilarCarsLength === 0}>
                Más parecidos ({mostSimilarCarsLength})
              </ToggleButton>
              <ToggleButton value="db" disabled={dbCarsLength === 0}>
                Base de datos ({dbCarsLength})
              </ToggleButton>
              <ToggleButton value="chileautos" disabled={chileautosCarsLength === 0}>
                ChileAutos ({chileautosCarsLength})
              </ToggleButton>
              <ToggleButton value="mercadolibre" disabled={mercadolibreCarsLength === 0}>
                MercadoLibre ({mercadolibreCarsLength})
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Analysis Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Eje X
              </Typography>
              <ToggleButtonGroup
                value={attribute}
                exclusive
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  setAttribute(newValue as Attribute);
                }}
                size="small"
              >
                <ToggleButton value="km">Kilometraje</ToggleButton>
                <ToggleButton value="usageYears">Años de uso</ToggleButton>
                <ToggleButton value="combined">Km + Años</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Análisis
              </Typography>
              <ToggleButtonGroup
                value={customAnalysis ? 'custom' : 'auto'}
                exclusive
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  const isCustom = newValue === 'custom';
                  setCustomAnalysis(isCustom);
                  if (!isCustom && data?.car) {
                    setCarPrice(data.car.price);
                    setCarKm(data.car.km);
                    setCarUsageYears(data.car.usageYears || 0);
                  }
                  if (isCustom) {
                    setCarUsageYears(Math.round(data.car.usageYears || 0));
                  }
                }}
                size="small"
              >
                <ToggleButton value="auto">Automático</ToggleButton>
                <ToggleButton value="custom">Personalizado</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Custom Analysis Fields */}
          {customAnalysis && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                backgroundColor: SURFACE_CONTAINER_LOW,
                borderRadius: 2,
              }}
            >
              <TextField
                label="Precio"
                type="number"
                value={carPrice}
                onChange={(e) => setCarPrice(Number(e.target.value))}
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    startAdornment: '$',
                    inputProps: { step: 500_000, min: 1_000_000 },
                  },
                }}
              />
              <TextField
                label="Kilometraje"
                type="number"
                value={carKm}
                onChange={(e) => setCarKm(Number(e.target.value))}
                fullWidth
                size="small"
                slotProps={{
                  htmlInput: { step: 10_000, min: 0 },
                }}
              />
              <TextField
                label="Año"
                type="number"
                value={2025 - carUsageYears}
                onChange={(e) => setCarUsageYears(2025 - Number(e.target.value))}
                fullWidth
                size="small"
                slotProps={{
                  htmlInput: { step: 1, min: 2000, max: 2025 },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Summary Stats Row */}
        <Box sx={{ display: 'flex', gap: 2.5, mb: 3.5 }}>
          <Box
            sx={{
              flex: 1,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Precio Promedio
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
              {priceFormat.format(results.averagePrice)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Autos Comparados
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>{results.cars.length}</Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              R² Regresión
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: confidenceColor }}>
              {rSquared.toFixed(3)}
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        {results.cars.length > 0 && (
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              mb: 3.5,
              boxShadow: SHADOW,
            }}
          >
            <Box sx={{ height: 400 }}>
              <ScatterChart
                xAxis={[
                  {
                    id: attribute,
                    label: getXAxisLabel(attribute),
                    valueFormatter: (v: number) => {
                      if (attribute === 'combined') {
                        return numberFormat(-10 * v);
                      }
                      return numberFormat(v);
                    },
                    reverse: attribute === 'combined',
                  },
                ]}
                yAxis={[
                  {
                    id: 'price',
                    label: '',
                    valueFormatter: (v: number) => priceFormat.format(v),
                  },
                ]}
                margin={{ left: 80 }}
                series={[
                  ...(manualCars.length > 0
                    ? [
                        {
                          id: 'cars-manual',
                          data: manualCars.map((c) => ({
                            id: `${c.link}-${c.km}-${c.usageYears}`,
                            x: c.x,
                            y: c.price || 0,
                          })),
                          label: 'Autos manuales',
                          color: CHART_COLOR_MANUAL,
                        },
                      ]
                    : []),
                  ...(automaticCars.length > 0
                    ? [
                        {
                          id: 'cars-automatic',
                          data: automaticCars.map((c) => ({
                            id: `${c.link}-${c.km}-${c.usageYears}`,
                            x: c.x,
                            y: c.price || 0,
                          })),
                          label: 'Autos automáticos',
                          color: CHART_COLOR_AUTOMATIC,
                        },
                      ]
                    : []),
                  {
                    id: 'current',
                    data: [
                      {
                        id: 'current',
                        x: calculateXValue({ km: carKm, usageYears: carUsageYears }),
                        y: carPrice,
                      },
                    ],
                    label: 'Auto actual',
                    color: CHART_COLOR_CURRENT,
                  },
                  {
                    id: 'regression',
                    data: (() => {
                      const minAttr = Math.min(
                        calculateXValue(data.car),
                        ...manualCars.map((c) => c.x),
                        ...automaticCars.map((c) => c.x)
                      );
                      const maxAttr = Math.max(
                        calculateXValue(data.car),
                        ...manualCars.map((c) => c.x),
                        ...automaticCars.map((c) => c.x)
                      );
                      const points = [];
                      const numPoints = 80;
                      for (let i = 0; i <= numPoints; i += 1) {
                        const attr = minAttr + (maxAttr - minAttr) * (i / numPoints);
                        points.push({
                          id: `regression-${i}`,
                          x: attr,
                          y: Math.round(calculateRegressionY(attr)),
                        });
                      }
                      return points;
                    })(),
                    label: 'Línea de regresión',
                    color: CHART_COLOR_REGRESSION,
                    markerSize: 1,
                  },
                ]}
                height={400}
                slots={{
                  tooltip: CustomTooltip,
                }}
                onItemClick={handleItemClick}
              />
            </Box>
          </Box>
        )}

        {/* Regressions — collapsible */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            mb: 3.5,
            boxShadow: SHADOW,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
              transition: 'background-color 0.15s ease',
            }}
            onClick={() => setShowRegressions(!showRegressions)}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Detalle de Regresiones
            </Typography>
            <IconButton size="small">
              <ExpandMoreIcon
                sx={{
                  transform: showRegressions ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          </Box>
          <Collapse in={showRegressions}>
            <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ p: 2, backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Kilometraje
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {priceFormat.format(Math.round(results.linearRegressionKm.intercept))} +{' '}
                  {priceFormat.format(Math.round(results.linearRegressionKm.slope))} x KM. El precio baja{' '}
                  {priceFormat.format(-Math.round(results.linearRegressionKm.slope) * 10_000)} por cada 10.000 km.
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Años de uso
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {priceFormat.format(Math.round(results.linearRegressionUsageYears.intercept))} +{' '}
                  {priceFormat.format(Math.round(results.linearRegressionUsageYears.slope))} x Años. El precio baja{' '}
                  {priceFormat.format(-Math.round(results.linearRegressionUsageYears.slope))} por cada año de uso.
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: SURFACE_CONTAINER_LOW, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Kilometraje + Años de uso
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {priceFormat.format(Math.round(results.multiLinearRegression.intercept))} +{' '}
                  {priceFormat.format(Math.round(results.multiLinearRegression.slopeKm))} x KM +{' '}
                  {priceFormat.format(Math.round(results.multiLinearRegression.slopeUsageYears))} x Años. El precio baja{' '}
                  {priceFormat.format(-Math.round(results.multiLinearRegression.slopeKm) * 10_000)} por cada 10.000 km +{' '}
                  {priceFormat.format(-Math.round(results.multiLinearRegression.slopeUsageYears))} por cada año de uso.
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Comparison Car List */}
        {results.cars.length > 0 && (
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              boxShadow: SHADOW,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Autos comparados ({results.cars.length})
              </Typography>
            </Box>

            {/* Current car highlight */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: SURFACE_CONTAINER_LOW,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Chip label="Actual" size="small" color="error" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                {capitalize(
                  `${data.car.year} ${numberFormat(data.car.km)}km — ${priceFormat.format(data.car.price)}. ${data.car.brand} ${data.car.model} ${data.car.version || ''} ${data.car.transmission} ${data.car.fuelType}`
                )}
              </Typography>
              {data.car.link && (
                <Link href={data.car.link} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.8rem' }}>
                  <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                </Link>
              )}
            </Box>

            {/* Car rows */}
            {[...results.cars]
              .sort((a, b) =>
                attribute === 'km' ? (a.km || 0) - (b.km || 0) : (a.usageYears || 0) - (b.usageYears || 0)
              )
              .map((c, index) => (
                <Box
                  key={`car-${index + 1}-${c.link}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 3,
                    py: 1,
                    backgroundColor: index % 2 === 0 ? SURFACE_CONTAINER_LOWEST : SURFACE_CONTAINER_LOW,
                    transition: 'background-color 0.15s ease',
                    '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {capitalize(
                      `${c.year} ${numberFormat(c.km || 0)}km — ${priceFormat.format(c.price || 0)}. ${c.brand} ${c.model} ${c.transmission} ${c.fuelType}`
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    {c.link && (
                      <Link
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}
                      >
                        <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                      </Link>
                    )}
                    {c.id && (
                      <Link
                        href={`/cars?id=${c.id}&analyzePrice=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                      >
                        Analizar
                      </Link>
                    )}
                  </Box>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceAnalysisModal;
